import { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Dialog, DialogContent, Button, Box } from '@mui/material';
import { Control, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useOnSaved } from '../../../../hooks/useOnSaved';
import * as lookupSlice from '../../../../data/lookup.slice';
import * as clientSlice from '../../../../data/client.slice';
import * as uiSlice from '../../../../data/ui.slice';
import TextInput from '../../../forms/TextInput';
import SelectMenu from '../../../forms/SelectMenu';
import { TransportationBenefit } from '../../../../data/models/transportation-benefit.model';
import { TransportationBenefitFunction } from '../../../../data/models/transportation-benefit-function.model';
import {
  Operand as OperandType,
  TransportationBenefitModel,
} from '../../../../data/models/transportation-benefit-model.model';
import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import ChipSelectMenu from '../../../forms/ChipSelectMenu';
import { PassengerAddressUse } from '../../../../data/models/passenger_address_use.model';
import FullScreenDialogHeader from '../FullScreenDialogHeader';
import { useNavigate } from 'react-router-dom';

const PREFIX = 'TransportationBenefitDialog';

const classes = {
  content: `${PREFIX}-content`,
  message: `${PREFIX}-message`,
  row: `${PREFIX}-row`,
  ctrl: `${PREFIX}-ctrl`,
  contentPaper: `${PREFIX}-contentPaper`,
  grid: `${PREFIX}-grid`,
  actions: `${PREFIX}-actions`,
  formControl: `${PREFIX}-formControl`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    paddingLeft: theme.spacing(18),
    paddingRight: theme.spacing(18),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.contentPaper}`]: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    borderRadius: 3,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(4),
    height: 'fit-content',
    width: '564px',
  },

  [`& .${classes.formControl}`]: {
    width: '394px',
  },

  [`& .${classes.message}`]: {
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.row}`]: {
    width: '100%',
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: 'center',
  },

  [`& .${classes.ctrl}`]: {
    flexGrow: 1,
  },

  [`& .${classes.actions}`]: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(3),
    gap: theme.spacing(3),
  },
}));

const Params = ({
  benefitFunction,
  control,
  requiredFieldMessage,
  namedLocations,
  passgenerAddressUses,
  t,
}: {
  benefitFunction?: TransportationBenefitFunction;
  control: Control<any>;
  requiredFieldMessage: string;
  namedLocations: any[];
  passgenerAddressUses: PassengerAddressUse[];
  t: any;
}) => {
  // TODO: this is hard coded for now. The selectable time periods will be provided via API in the future
  const tripCountPeriods = [
    { label: 'Service Plan', value: 'SERVICEPLAN' },
    { label: 'Calendar Month', value: 'CALENDARMONTH' },
    { label: 'Calendar Year', value: 'CALENDARYEAR' },
  ];

  return (
    <>
      {benefitFunction?.parameters.map((p) => {
        const name = `parameters.${p.name}`;
        if (p.name === 'tripCountPeriod') {
          return (
            <SelectMenu
              label={t('client.tripCountPeriod')}
              name={name}
              values={tripCountPeriods}
              valueAttr="value"
              labelAttr="label"
              required={true}
              control={control}
              rules={{ required: requiredFieldMessage }}
            />
          );
        } else if (p.type === 'UserSupplied') {
          return (
            <TextInput
              key={name}
              label={t(`client.${p.name}`)}
              name={name}
              required={true}
              control={control}
              rules={{ required: requiredFieldMessage }}
            />
          );
        } else if (p.type === 'PurposeCodes') {
          const purposeCodes = useAppSelector(clientSlice.selectPurposeCodes);
          return (
            <ChipSelectMenu
              key={name}
              label={t(`client.${p.name}`)}
              name={name}
              className={classes.ctrl}
              values={purposeCodes.filter((p) => p.state === 'active')}
              valueAttr="purposeCodeUUID"
              labelAttr="name"
              required={true}
              control={control}
              rules={{ required: requiredFieldMessage }}
            />
          );
        } else if (p.type === 'NamedLocations') {
          return (
            <ChipSelectMenu
              label={t('client.namedLocations')}
              name={name}
              key={name}
              values={namedLocations}
              valueAttr="locationUUID"
              labelAttr="address"
              required={true}
              control={control}
              rules={{ required: requiredFieldMessage }}
            />
          );
        } else if (p.name === 'passengerAddressUseBCs') {
          return (
            <ChipSelectMenu
              label={t('client.passengerAddressUse')}
              name={name}
              key={name}
              values={passgenerAddressUses}
              valueAttr="passengerAddressUseBC"
              labelAttr="passengerAddressUseName"
              required={true}
              control={control}
              rules={{ required: requiredFieldMessage }}
            />
          );
        }
      })}
    </>
  );
};

const Function = ({
  benefitFunctions,
  level,
  name,
  control,
  requiredFieldMessage,
  namedLocations,
  passgenerAddressUses,
  t,
}: {
  benefitFunctions: TransportationBenefitFunction[];
  level: number;
  name: string;
  control: Control<any>;
  requiredFieldMessage: string;
  namedLocations: any[];
  passgenerAddressUses: PassengerAddressUse[];
  t: any;
}) => {
  const func = benefitFunctions.find(
    (f) => f.transportationBenefitFunctionBC === name,
  );

  return (
    <Box
      component="div"
      className={classes.content}
      style={{ paddingLeft: level * 4 }}>
      {func?.name}
      <Params
        control={control}
        benefitFunction={func}
        requiredFieldMessage={requiredFieldMessage}
        namedLocations={namedLocations}
        passgenerAddressUses={passgenerAddressUses}
        t={t}
      />
    </Box>
  );
};

const Operand = ({
  benefitFunctions,
  operand,
  level = 0,
  control,
  requiredFieldMessage,
  namedLocations,
  passgenerAddressUses,
}: {
  benefitFunctions: TransportationBenefitFunction[];
  operand: OperandType;
  level?: number;
  control: Control<any>;
  requiredFieldMessage: string;
  namedLocations: any[];
  passgenerAddressUses: PassengerAddressUse[];
}) => {
  const t = useCommonTranslation();
  let curLevel = level + 1;

  if (Object.keys(operand).includes('literal')) {
    return (
      <Box
        component="div"
        className={classes.row}
        style={{ paddingLeft: curLevel * 4 }}>
        {(operand as { literal: boolean }).literal ? 'true' : 'false'}{' '}
      </Box>
    );
  }

  if (Object.keys(operand).includes('function')) {
    const fName = (operand as { function: string }).function;
    return (
      <Function
        benefitFunctions={benefitFunctions}
        level={curLevel}
        name={fName}
        control={control}
        requiredFieldMessage={requiredFieldMessage}
        t={t}
        namedLocations={namedLocations}
        passgenerAddressUses={passgenerAddressUses}
      />
    );
  }

  if (Object.keys(operand).includes('operator')) {
    return (
      <Box component="div" style={{ paddingLeft: curLevel * 4 }}>
        {(operand as { operator: string }).operator.toUpperCase()}
        {(
          operand as { operator: string; operands: OperandType[] }
        ).operands.map((op, idx) => (
          <Box component="span" className={classes.row} key={`op-${idx}`}>
            <Operand
              benefitFunctions={benefitFunctions}
              operand={op}
              level={curLevel}
              control={control}
              requiredFieldMessage={requiredFieldMessage}
              namedLocations={namedLocations}
              passgenerAddressUses={passgenerAddressUses}
            />
          </Box>
        ))}
      </Box>
    );
  }

  return null;
};

interface TransportationBenefitDialogProps {
  title: string;
  clientBC: string;
  transportationBenefit?: TransportationBenefit;
  open: boolean;
  close: () => void;
}

export default function TransportationBenefitDialog({
  title,
  clientBC,
  transportationBenefit,
  open,
  close,
}: TransportationBenefitDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const benefitPurposes = useAppSelector(lookupSlice.selectBenefitPurposes);
  const linesOfBusiness = useAppSelector(lookupSlice.selectLinesOfBusiness);
  const transportationBenefitModels = useAppSelector(
    clientSlice.selectActiveTransportationBenefitModels,
  );
  const transportationBenefitFunctions = useAppSelector(
    clientSlice.selectTransportationBenefitFunctions,
  );
  const savingTransportationBenefit = useAppSelector(
    clientSlice.selectSavingTransportationBenefit,
  );
  const savedTransportationBenefit = useAppSelector(
    clientSlice.selectSavedTransportationBenefit,
  );
  const locations = useAppSelector(clientSlice.selectClientLocations);
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const namedLocationsList = useAppSelector(clientSlice.selectNamedLocations);
  const passgenerAddressUses = useAppSelector(
    lookupSlice.selectPassengerAddressUses,
  );
  const client = useAppSelector(clientSlice.selectCurrentClient);
  const transportationBenefits = useAppSelector(
    clientSlice.selectTransportationBenefits,
  );
  const requiredFieldMessage = t('common.requiredField');

  const namedLocations = namedLocationsList.map((location) => ({
    address: `${(location as any).addressLine1}, ${(location as any).city}, ${(location as any).stateCode}, ${(location as any).zipCode}`,
    locationUUID: location.namedLocationUUID,
    locationName: location.name,
  }));

  const [benefitModel, setBenefitModel] =
    useState<TransportationBenefitModel>();

  const formHook = useForm({
    defaultValues: {
      name: '',
      description: '',
      benefitPurpose: '',
      lineOfBusiness: '',
      priority: 1,
      benefitModel: '',
      parameters: {},
    },
  });

  useEffect(() => {
    dispatch(clientSlice.fetchClientLocationsAsync(clientBC));
    dispatch(lookupSlice.fetchLookupItemsAsync('passengerAddressUse'));
  }, [dispatch]);

  useEffect(() => {
    const namedLocationUUIDs = locations.map((l) => l.namedLocationUUID);
    if (namedLocationUUIDs.length) {
      dispatch(clientSlice.fetchNamedLocationsByUUIDsAsync(namedLocationUUIDs));
    }
  }, [dispatch, locations]);

  const currentBenefitParameters = useMemo(() => {
    return (
      transportationBenefit?.functionParameters.reduce(
        (params, { name, value }) => ({
          ...params,
          [name]: value,
        }),
        {},
      ) ?? {}
    );
  }, [transportationBenefit]);

  useEffect(() => {
    formHook.reset({
      name: transportationBenefit?.name || '',
      description: transportationBenefit?.description || '',
      benefitPurpose: transportationBenefit?.benefitPurposeBC || '',
      lineOfBusiness: transportationBenefit?.lineOfBusinessBC || '',
      priority: transportationBenefit?.priority || 1,
      benefitModel: transportationBenefit?.transportationBenefitModelUUID || '',
      parameters: currentBenefitParameters,
    });
  }, [open, transportationBenefit]);

  const selectedBenefitModel = formHook.watch('benefitModel');
  useEffect(() => {
    if (
      transportationBenefit?.transportationBenefitModelUUID ===
      selectedBenefitModel
    ) {
      formHook.resetField('parameters', {
        defaultValue: currentBenefitParameters,
      });
    } else {
      formHook.resetField('parameters', { defaultValue: {} });
    }
    if (selectedBenefitModel) {
      const bm = transportationBenefitModels.find(
        (b) => b.transportationBenefitModelUUID === selectedBenefitModel,
      );
      setBenefitModel(bm);
    } else {
      setBenefitModel(undefined);
    }
  }, [selectedBenefitModel]);

  useOnSaved(savedTransportationBenefit, () => {
    if (!open) return;
    handleClose();
    if (saveSuccessMessage === 'snackbar.taskComplete') {
      navigate(`/clients/${clientBC}/transportation-benefits`);
    }
  });

  const handleSave = (data: any) => {
    const newBenefit: TransportationBenefit = {
      clientBC,
      transportationBenefitUUID:
        transportationBenefit?.transportationBenefitUUID || undefined,
      name: data.name,
      description: data.description,
      benefitPurposeBC: data.benefitPurpose,
      lineOfBusinessBC: data.lineOfBusiness,
      transportationBenefitModelUUID: data.benefitModel,
      priority: parseInt(data.priority, 10),
      functionParameters: Object.entries(data.parameters).map(
        ([name, value]) => {
          const parsedNumber = Number(value);
          return {
            name,
            value: isNaN(parsedNumber) ? value : parsedNumber,
          };
        },
      ),
    };
    if (transportationBenefit) {
      dispatch(clientSlice.updateTransportationBenefitAsync(newBenefit));
    } else {
      dispatch(
        clientSlice.createTransportationBenefitAsync(newBenefit, {
          isFirst: !transportationBenefits.length,
        }),
      );
    }
  };

  const handleClose = () => {
    formHook.reset();
    close();
  };

  return (
    <RootDialog open={open} onClose={handleClose} fullScreen>
      <FullScreenDialogHeader
        clientName={client?.legalName || ''}
        title={title}
        close={handleClose}
      />
      <DialogContent className={classes.content}>
        <div className={classes.contentPaper}>
          <SelectMenu
            label={t('client.benefitModel')}
            name="benefitModel"
            values={transportationBenefitModels}
            valueAttr="transportationBenefitModelUUID"
            labelAttr="name"
            required={true}
            control={formHook.control}
            rules={{ required: requiredFieldMessage }}
            className={classes.formControl}
          />
          {benefitModel && (
            <Operand
              benefitFunctions={transportationBenefitFunctions}
              operand={benefitModel.expression}
              control={formHook.control}
              requiredFieldMessage={requiredFieldMessage}
              namedLocations={namedLocations}
              passgenerAddressUses={passgenerAddressUses}
            />
          )}
          <TextInput
            name="name"
            label={t('client.transportationBenefitName')}
            control={formHook.control}
            required={true}
            rules={{ required: requiredFieldMessage }}
            errors={formHook.formState.errors}
            className={classes.formControl}
          />
          <TextInput
            name="description"
            label={t('client.transportationBenefitDescription')}
            control={formHook.control}
            required={true}
            errors={formHook.formState.errors}
            rules={{ required: requiredFieldMessage }}
            className={classes.formControl}
          />
          <SelectMenu
            label={t('client.benefitPurpose')}
            name="benefitPurpose"
            values={benefitPurposes}
            valueAttr="benefitPurposeBC"
            labelAttr="benefitPurposeName"
            control={formHook.control}
            required={true}
            rules={{ required: requiredFieldMessage }}
            className={classes.formControl}
          />
          <SelectMenu
            label={t('client.lineOfBusiness')}
            name="lineOfBusiness"
            values={linesOfBusiness}
            valueAttr="lineOfBusinessBC"
            labelAttr="lineOfBusinessName"
            control={formHook.control}
            required={true}
            rules={{ required: requiredFieldMessage }}
            className={classes.formControl}
          />
          <TextInput
            label={t('client.priority')}
            type="number"
            name="priority"
            required={true}
            control={formHook.control}
            inputProps={{ maxLength: 3, min: 1, max: 999 }}
            className={classes.formControl}
          />
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              disabled={
                !formHook.formState.isValid || savingTransportationBenefit
              }
              onClick={formHook.handleSubmit(handleSave)}>
              {transportationBenefit ? t('common.update') : t('common.add')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={savingTransportationBenefit}
              onClick={handleClose}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </RootDialog>
  );
}
